import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { STATISTIC_CONST } from '@/constant';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  VDatePicker,
  VFlex,
  VIconButton,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
} from '@/package';
import { getMonthWeekInfo } from '@/util';

import type { DayRange, MonthRange, PeriodUnit, WeekRange } from './type';

export const RangePicker = ({
  value,
  onChange,
  periodUnit,
}: {
  value: [Dayjs, Dayjs];
  onChange: (value: [Dayjs, Dayjs]) => void;
  periodUnit: PeriodUnit;
}) => {
  const tabList = (() => {
    if (periodUnit === 'day') {
      return [
        { label: '15D', value: '15D' },
        { label: '30D', value: '30D' },
        { label: 'custom', value: 'custom' },
      ];
    } else if (periodUnit === 'week') {
      return [
        { label: '4W', value: '4W' },
        { label: '8W', value: '8W' },
        { label: 'custom', value: 'custom' },
      ];
    } else {
      return [
        { label: '6M', value: '6M' },
        { label: '1Y', value: '1Y' },
        { label: 'custom', value: 'custom' },
      ];
    }
  })();
  const [rangePivot, setRangePivot] = useState<
    WeekRange | MonthRange | DayRange | 'custom'
  >(tabList[0].value as WeekRange | MonthRange | DayRange | 'custom');

  const isCustom = rangePivot === 'custom';

  useEffect(() => {
    if (rangePivot === '15D') {
      onChange([dayjs().subtract(15, 'day'), dayjs()]);
    } else if (rangePivot === '30D') {
      onChange([dayjs().subtract(30, 'day'), dayjs()]);
    } else if (rangePivot === '4W') {
      const { thisWeekMonday, thisWeekSunday } = getMonthWeekInfo(dayjs());
      onChange([thisWeekMonday.subtract(3, 'week'), thisWeekSunday]);
    } else if (rangePivot === '8W') {
      const { thisWeekMonday, thisWeekSunday } = getMonthWeekInfo(dayjs());
      onChange([thisWeekMonday.subtract(7, 'week'), thisWeekSunday]);
    } else if (rangePivot === '6M') {
      onChange([dayjs().subtract(6, 'month'), dayjs()]);
    } else if (rangePivot === '1Y') {
      onChange([dayjs().subtract(1, 'year'), dayjs()]);
    }
  }, [rangePivot]);

  return (
    <VFlex items="center" gap={0.25}>
      <VIconButton
        Icon={KeyboardArrowLeft}
        size="md"
        variant="roundPrimary"
        onClick={() => {
          const [start, end] = value;
          onChange([
            start.subtract(1, periodUnit),
            end.subtract(1, periodUnit),
          ]);
        }}
      />
      <VTab
        defaultValue="day"
        value={rangePivot}
        onValueChange={val =>
          setRangePivot(val as WeekRange | MonthRange | DayRange | 'custom')
        }
        variant="secondary"
        size="small"
      >
        <VTabTriggerContainer>
          <VTabTrigger value={tabList[0].value}>{tabList[0].label}</VTabTrigger>
          <VTabTrigger value={tabList[1].value}>{tabList[1].label}</VTabTrigger>
          <VTabTrigger value={tabList[2].value}>{tabList[2].label}</VTabTrigger>
        </VTabTriggerContainer>
      </VTab>
      <VDatePicker
        className="[&_input]:!w-[120px]"
        mode="range"
        disabled={!isCustom}
        size="md"
        value={value as any}
        onChange={val => {
          const [_start, _end] = val as unknown as [Date, Date];
          const start = dayjs(_start);
          const end = dayjs(_end);
          if (periodUnit === 'week') {
            const { thisWeekMonday: startWeekMonday } = getMonthWeekInfo(start);
            const { thisWeekSunday: endWeekSunday } = getMonthWeekInfo(end);

            onChange([startWeekMonday, endWeekSunday]);
          } else {
            onChange([start, end]);
          }
        }}
      />
      <VIconButton
        Icon={KeyboardArrowRight}
        size="md"
        variant="roundPrimary"
        disabled={value[0].add(1, periodUnit).isAfter(STATISTIC_CONST.TODAY)}
        onClick={() => {
          const [start, end] = value;
          onChange([start.add(1, periodUnit), end.add(1, periodUnit)]);
        }}
      />
    </VFlex>
  );
};
